export const SORT_OPTIONS = [
  { value: "newest", label: "Më të rejat" },
  { value: "lowToHigh", label: "Çmimi: ulët → lartë" },
  { value: "highToLow", label: "Çmimi: lartë → ulët" },
];

export const GENDER_OPTIONS = [
  { value: "FEMALE", label: "Femër" },
  { value: "MALE", label: "Mashkull" },
  { value: "KIDS", label: "Fëmijë" },
];

export const PRICE_PRESETS = [
  { id: "under-50", label: "Nën 50 €", min: 0, max: 50 },
  { id: "50-100", label: "50–100 €", min: 50, max: 100 },
  { id: "100-200", label: "100–200 €", min: 100, max: 200 },
  { id: "over-200", label: "Mbi 200 €", min: 200, max: undefined },
];

export const SIZE_GROUPS = [
  {
    id: "clothes",
    label: "Rroba",
    sizes: ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "4XL"],
  },
  {
    id: "shoes",
    label: "Këpucë",
    sizes: ["35", "36", "37", "37½", "38", "38½", "39", "40", "41", "42", "43", "44", "45", "46", "5", "6", "6½", "7", "7½", "8", "8½", "9", "9½"],
  },
  {
    id: "other",
    label: "Të tjera",
    sizes: ["1", "2", "3", "OS", "80", "85", "90", "95", "100", "105", "110", "115"],
  },
];

export const FILTER_BRANDS = [
  "ANTONY MORATO",
  "ARMANI EXCHANGE",
  "BOGGI",
  "BOSS",
  "CALVIN KLEIN JEANS",
  "CALVIN KLEIN UNDERWEAR",
  "CARPISA",
  "CELIO",
  "DAN JOHN",
  "EMPORIO ARMANI",
  "GANT",
  "GEOX",
  "GUESS",
  "GUESS ACCESSORIES",
  "GUESS JEANS",
  "HUGO",
  "KARACA",
  "MANGO",
  "OKAIDI",
  "OLIVER WEBER",
  "OZDILEK",
  "PARFOIS",
  "PLEIN SPORT",
  "REPLAY",
  "SPRINGFIELD",
  "STILIUM",
  "TIMBERLAND",
  "TOM TAILOR",
  "WOMEN SECRET",
];

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const sortLabel = (activeSort) => {
  if (!activeSort || activeSort === "newest") return "Më të rejat";
  return SORT_OPTIONS.find((option) => option.value === activeSort)?.label || "Më të rejat";
};

export const isSortSelected = (activeSort, value) => {
  if (value === "newest") return !activeSort || activeSort === "newest";
  return activeSort === value;
};

export const genderLabel = (value) => {
  if (!value) return "";
  const normalized = String(value).toLowerCase();
  if (normalized === "unisex") return "Unisex";
  const match = GENDER_OPTIONS.find(
    (option) => option.value.toLowerCase() === normalized
  );
  return match?.label || String(value);
};

export const isGenderSelected = (activeGender, value) =>
  String(activeGender || "").toLowerCase() === String(value || "").toLowerCase();

export const isSameIdList = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  return (
    left.length === right.length &&
    left.every((id, index) => String(id) === String(right[index]))
  );
};

export const isCategorySelected = (activeCategories, categoryId) => {
  if (!categoryId) return false;
  const active = Array.isArray(activeCategories) ? activeCategories : [];

  if (Array.isArray(categoryId)) {
    return active.some((entry) => Array.isArray(entry) && isSameIdList(entry, categoryId));
  }

  return active.flat().some((id) => String(id) === String(categoryId));
};

export const isPresetActive = (min, max, preset) => {
  const currentMin = toNumber(min);
  const currentMax = toNumber(max);
  const presetMin = preset.min === undefined ? undefined : preset.min;
  const presetMax = preset.max === undefined ? undefined : preset.max;
  return currentMin === presetMin && currentMax === presetMax;
};

export const formatPriceLabel = (min, max) => {
  const currentMin = toNumber(min);
  const currentMax = toNumber(max);
  if (currentMin === undefined && currentMax === undefined) return "";

  const preset = PRICE_PRESETS.find((item) => isPresetActive(currentMin, currentMax, item));
  if (preset) return preset.label;
  if (currentMin !== undefined && currentMax !== undefined) return `${currentMin}–${currentMax} €`;
  if (currentMin !== undefined) return `Nga ${currentMin} €`;
  return `Deri ${currentMax} €`;
};

export const normalizeSizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace("1/2", "½")
    .replace(".5", "½");

export const sizesMatch = (left, right) => normalizeSizeKey(left) === normalizeSizeKey(right);

// Numeric sizes overlap across product types (shirt 41 vs shoe 41), so splitting them into
// Rroba/Këpucë/Numra is misleading. When every selected category sits under one main
// category, the sizes are shown as a single group named after it.
export const getSingleSizeGroupLabel = (catData = [], activeCategories = []) => {
  const selectedMains = catData.filter((mainCat) =>
    (mainCat.groupCategory || []).some((child) => isCategorySelected(activeCategories, child.linkedCategory))
  );
  if (selectedMains.length !== 1) return null;
  return mainCategoryLabel(selectedMains[0]);
};

const numericSizeOrder = (value) => {
  const parsed = parseFloat(String(value).replace("½", ".5"));
  return Number.isNaN(parsed) ? Infinity : parsed;
};

const sortSizes = (availableSizes = []) => {
  const letterOrder = SIZE_GROUPS.find((group) => group.id === "clothes").sizes.map(normalizeSizeKey);
  const rank = (size) => {
    const letterIndex = letterOrder.indexOf(normalizeSizeKey(size));
    if (letterIndex !== -1) return [0, letterIndex];
    const numeric = numericSizeOrder(size);
    return numeric === Infinity ? [2, 0] : [1, numeric];
  };
  const seen = new Set();
  return availableSizes
    .map((size) => String(size || "").trim())
    .filter((size) => {
      const key = normalizeSizeKey(size);
      if (!size || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const [groupA, orderA] = rank(a);
      const [groupB, orderB] = rank(b);
      return groupA - groupB || orderA - orderB || a.localeCompare(b);
    });
};

const mainCategoryLabel = (mainCat) => {
  const name = String(mainCat?.name || "").trim();
  return /^veshje$/i.test(name) ? "Rroba" : name || "Madhësitë";
};

// Groups sizes under the main category of the products that carry them, using the
// per-category size lists from /brand-facets. Only selected categories count when
// any are selected, so a stray category code on a product doesn't add a group.
export function groupSizesByMainCategory(sizesByCategory, catData = [], activeCategories = [], availableSizes = []) {
  if (!sizesByCategory || !catData.length) return null;
  const allowed = new Set(availableSizes.map(normalizeSizeKey));
  const hasSelection = catData.some((mainCat) =>
    (mainCat.groupCategory || []).some((child) => isCategorySelected(activeCategories, child.linkedCategory))
  );
  const used = new Set();

  const groups = catData.map((mainCat) => {
    const children = (mainCat.groupCategory || []).filter(
      (child) => !hasSelection || isCategorySelected(activeCategories, child.linkedCategory)
    );
    const ids = children.flatMap((child) =>
      Array.isArray(child.linkedCategory) ? child.linkedCategory : [child.linkedCategory]
    );
    const sizes = sortSizes(
      ids.flatMap((id) => sizesByCategory[String(id)] || [])
        .filter((size) => !allowed.size || allowed.has(normalizeSizeKey(size)))
    );
    sizes.forEach((size) => used.add(normalizeSizeKey(size)));
    return { id: String(mainCat._id || mainCat.name), label: mainCategoryLabel(mainCat), sizes };
  }).filter((group) => group.sizes.length);

  if (!hasSelection) {
    const extra = availableSizes.filter((size) => size && !used.has(normalizeSizeKey(size)));
    if (extra.length) groups.push({ id: "other", label: "Të tjera", sizes: sortSizes(extra) });
  }
  return groups;
}

export function groupAvailableSizes(availableSizes = [], { singleGroupLabel = null } = {}) {
  if (singleGroupLabel) {
    const sizes = sortSizes(availableSizes);
    return sizes.length ? [{ id: "single", label: singleGroupLabel, sizes }] : [];
  }
  return groupSizesInto(availableSizes, SIZE_GROUPS);
}

function groupSizesInto(availableSizes, sizeGroups) {
  const byKey = new Map();
  availableSizes.forEach((size) => {
    const raw = String(size || "").trim();
    if (!raw) return;
    const key = normalizeSizeKey(raw);
    if (!byKey.has(key)) byKey.set(key, raw);
  });

  const used = new Set();
  const groups = sizeGroups.map((group) => {
    const sizes = [];
    group.sizes.forEach((label) => {
      const key = normalizeSizeKey(label);
      if (!byKey.has(key)) return;
      sizes.push(byKey.get(key));
      used.add(key);
    });
    return { ...group, sizes };
  }).filter((group) => group.sizes.length);

  const extra = [...byKey.entries()].filter(([key]) => !used.has(key)).map(([, raw]) => raw);
  if (extra.length) {
    const other = groups.find((group) => group.id === "other");
    if (other) other.sizes.push(...extra);
    else groups.push({ id: "other", label: "Të tjera", sizes: extra });
  }

  return groups;
}

const GENDER_ALIASES = {
  female: "FEMALE",
  femër: "FEMALE",
  femer: "FEMALE",
  woman: "FEMALE",
  women: "FEMALE",
  f: "FEMALE",
  male: "MALE",
  mashkull: "MALE",
  man: "MALE",
  men: "MALE",
  m: "MALE",
  kids: "KIDS",
  kid: "KIDS",
  child: "KIDS",
  children: "KIDS",
  fëmijë: "KIDS",
  femije: "KIDS",
  unisex: "UNISEX",
  uni: "UNISEX",
};

export function filterGenderOptions(rawGenders = []) {
  const wanted = new Set();
  rawGenders.forEach((value) => {
    const mapped = GENDER_ALIASES[String(value || "").trim().toLowerCase()];
    if (mapped) wanted.add(mapped);
  });
  const options = GENDER_OPTIONS.filter((option) => wanted.has(option.value));
  if (wanted.has("UNISEX")) options.push({ value: "UNISEX", label: "Unisex" });
  return options;
}

export function categoryIsAvailable(linkedCategory, availableIds) {
  if (!availableIds) return true;
  const ids = Array.isArray(linkedCategory) ? linkedCategory : [linkedCategory];
  return ids.some((id) => id != null && id !== "" && availableIds.has(String(id)));
}

export const formatProductCount = (count) => {
  if (count === undefined || count === null || count === "") return "";
  const total = Number(count);
  if (Number.isNaN(total)) return "";
  return total === 1 ? "1 produkt" : `${total} produkte`;
};

export const buildActiveChips = ({
  catData = [],
  activeCategories = [],
  activeBrands = [],
  activeSubBrands = [],
  activeSizes = [],
  activeGender,
  activeOffer,
  activeMinPrice,
  activeMaxPrice,
}) => {
  const chips = [];

  catData.forEach((mainCat) => {
    (mainCat.groupCategory || []).forEach((child) => {
      if (!isCategorySelected(activeCategories, child.linkedCategory)) return;
      chips.push({
        id: `category-${child._id || child.name}`,
        label: child.name,
        kind: "category",
        payload: child.linkedCategory,
      });
    });
  });

  activeBrands.forEach((brand) => {
    chips.push({ id: `brand-${brand}`, label: brand, kind: "brand", payload: brand });
  });

  activeSubBrands.forEach((brand) => {
    chips.push({ id: `subbrand-${brand}`, label: brand, kind: "subbrand", payload: brand });
  });

  activeSizes.forEach((size) => {
    chips.push({ id: `size-${size}`, label: size, kind: "size", payload: size });
  });

  if (activeGender) {
    chips.push({
      id: "gender",
      label: genderLabel(activeGender),
      kind: "gender",
      payload: activeGender,
    });
  }

  if (String(activeOffer || "").toLowerCase() === "yes") {
    chips.push({ id: "offer", label: "Zbritje", kind: "offer" });
  }

  const priceLabel = formatPriceLabel(activeMinPrice, activeMaxPrice);
  if (priceLabel) {
    chips.push({ id: "price", label: priceLabel, kind: "price" });
  }

  return chips;
};

const flexibleBrandPattern = (value) => {
  return String(value || "")
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/[-\s]+/g, "[-\\s]+");
};

// "STILIUM-UGG" + slug "stilium" → "UGG". Viza dhe hapësira në emrin kryesor trajtohen njësoj.
export const subBrandFromProductBrand = (productBrand, storeSlug) => {
  const brand = String(productBrand || "").trim();
  const slug = String(storeSlug || "").trim();
  if (!brand || !slug) return "";
  const prefix = new RegExp(`^${flexibleBrandPattern(slug)}[-\\s]+`, "i");
  if (!prefix.test(brand)) return "";
  return brand.replace(prefix, "").trim().toUpperCase();
};

export const parseBrendiParam = (params) => {
  const values = typeof params?.getAll === "function" ? params.getAll("brendi") : [];
  const parts = values.flatMap((value) => String(value).split(","));
  return [...new Set(parts.map((part) => part.trim().toUpperCase()).filter(Boolean))];
};
